import '../../highlight';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import QuillCursors from '@minervaproject/quill-cursors';
import ReactQuill, { Quill } from 'react-quill-new';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import 'react-quill-new/dist/quill.snow.css';
import classnames from 'classnames';

import '../../css/quill.scss';
import { getColor } from '../../color';
import LargeFormula from './LargeFormula';
import CustomLinkSanitizer from '../../helpers/quill-link-sanitizer';

Quill.register('modules/cursors', QuillCursors);
Quill.register(LargeFormula);
Quill.register(CustomLinkSanitizer, true);

// to fix a bug with Quill deleting spell checked words in certain circumstances
// https://app.asana.com/0/1193572723551132/1198698499683760
const Inline = Quill.import('blots/inline');

/**
 * Quill 2.0 made some changes as to how the composition API is handled, which tries to batch
 * an entire IME composition (used in e.g. Chinese/Japanese/Korean) at once without triggering
 * onChange events.
 * This is a problem for us, as we rely on the fact that any change to the editor content immediately
 * triggers an onChange event. If we delay the event, and another user's input comes in, we detect the new
 * content that's still part of the composition as something we just wrote, and send it back to the server,
 * which is then duplicated once we actually commit the composition. Additionally, updates from other users
 * that arrive are seen as local updates, which also causes them to be duplicated back to the sender.
 * If many users are typing with IMEs in the same document, this becomes extremely messy.
 *
 * We tried many solutions (see https://github.com/minervaproject/structured-learning-exercises/pull/1586).
 * The simplest and hopefully lowest-risk solution we found was to patch the Quill instance to force it to
 * only tiny batches and immediately trigger onChange events. This doesn't prevent _all_ issues, but improves
 * the experience enough for it to be, at most, a minor annoyance.
 */
const maybePatchQuillCompositionAPI = (quill) => {
  if (!quill.compositionPatched) {
    quill.scroll.domNode.addEventListener('compositionupdate', () => {
      if (quill.composition.isComposing) {
        quill.scroll.batchEnd();
        quill.scroll.batchStart();
      }
    });
    quill.compositionPatched = true;
  }
};

class CustomAttributes extends Inline {
  constructor(domNode, value) {
    super(domNode, value);

    const span = this.replaceWith(new Inline(Inline.create()));

    span.children.forEach((child) => {
      if (child.attributes) child.attributes.copy(span);
      if (child.unwrap) child.unwrap();
    });

    // here we apply every attribute from <font> tag to span as a style
    Object.keys(domNode.attributes).forEach((key) => {
      if (domNode.attributes[key].name !== 'style') {
        const value = domNode.attributes[key].value;
        let name = domNode.attributes[key].name;
        if (name === 'face') name = 'font-family';
        span.format(name, value);
      }
    });

    this.remove();

    return span;
  }
}

CustomAttributes.blotName = 'customAttributes';
CustomAttributes.tagName = 'FONT';

Quill.register(CustomAttributes, true);
// spell check fix end

const reducePush = (ary, item, maxItems) => {
  ary.push(item);
  if (ary.length > maxItems) {
    ary.splice(0, ary.length - maxItems);
  }
};

// avoid pasting of images which are naively inserted into ShareDB doc causing memory issues,
// TODO: fix properly with upload to S3
// list from https://github.com/quilljs/quill/issues/1108
const formats = [
  'background',
  'bold',
  'color',
  'font',
  'code',
  'italic',
  'link',
  'size',
  'strike',
  'script',
  'underline',
  'blockquote',
  'header',
  'indent',
  'list',
  'align',
  'direction',
  'code-block',
  'formula',
  // 'image'
  // 'video'
];

// There is a Rollbar bug that we have had huge problems reproducing, partially because we don't
// have access to the presence messages. Using this global presenceLog object, which gets sent
// to Rollbar on error, we hope to be able to reproduce and fix this bug in the future.
// (Stian Sep 20, 2019)
const presenceLog = [];

// We use userIDs internally to track cursors, but when we receive a disconnect we only receive
// the ShareDB auto-generated session id. Therefore, we make sure to track the mapping between
// userId and ShareDB session Id when we first setup a new cursor, so that we can use this later
// to track which cursor to remove on disconnect.
const IDMapping = {};

class RichTextField extends Component {
  constructor(props) {
    super(props);
    const { path, uuid } = this.props;

    this.path = path || [uuid];
  }

  update = () => {
    const { doc } = this.props;
    const data = get(doc.data, this.path);

    // Bug fix: Quill ignores the last newline, which causes problems
    // with rendering and capturing the latest caption
    if (data && data.ops) {
      data.ops = [...data.ops, { insert: '\n' }];
    }

    this._editor.getEditor().setContents(data);
  };

  updateCursor = (range) => {
    const { readOnlyWorksheet } = this.props;

    if (readOnlyWorksheet) {
      return;
    }
    const {
      doc,
      currentUser: { userId },
    } = this.props;
    doc.submitPresence({
      p: this.path,
      t: 'rich-text',
      u: userId,
      s: {
        u: userId,
        c: 0,
        s: [[range.index, range.index + range.length]],
      },
    });
  };

  setUpCursors = () => {
    const { doc, currentUser } = this.props;
    const editor = this._editor.getEditor();
    this.cursors = editor.getModule('cursors');

    if (!currentUser.noPresence) {
      editor.on('selection-change', (range, _, source) => {
        if (range && source === 'user') {
          this.updateCursor(range);
        }
      });
    }

    doc.on('presence', this.onPresence);
  };

  onPresence = (srcList, submitted) => {
    const { doc, currentUser } = this.props;

    reducePush(presenceLog, doc.presence, 30);
    srcList.forEach((src) => {
      const presence = doc.presence[src];
      if (!presence || !presence.p) {
        this.cursors.removeCursor(IDMapping[src]);
        return;
      }

      if (!isEqual(presence.p, this.path)) {
        this.cursors.removeCursor(presence.u);
        return;
      }

      if (presence.s.u) {
        const userId = presence.s.u;
        IDMapping[src] = userId;
        if (
          userId !== currentUser.userId &&
          presence.s.s &&
          presence.s.s.length > 0
        ) {
          // TODO: Can QuillCursors support multiple selections?
          const sel = presence.s.s[0];

          // Use Math.abs because the sharedb presence type
          // supports reverse selections, but I don't think
          // Quill Cursors does.
          const len = Math.abs(sel[1] - sel[0]);
          const min = Math.min(sel[0], sel[1]);

          try {
            this.cursors.createCursor(
              userId,
              doc.data.users[userId].name,
              getColor(userId)
            );
            this.cursors.moveCursor(userId, { index: min, length: len });
            if (submitted) {
              this.cursors.flashCursor(userId);
            }
          } catch (e) {
            if (window.Rollbar) {
              window.Rollbar.error(
                'Error while updating cursors/selections',
                e,
                {
                  userId,
                  index: len,
                  length: min,
                  data: doc.data,
                  presence: presence,
                  docPresence: doc.presence,
                  presenceLog,
                }
              );
            }
          }
        }
      }
    });
  };

  componentDidMount() {
    const { currentUser } = this.props;

    // if we do not want to listen to the ShareDB doc but receive
    // props like a normal component
    if (currentUser.timeline) {
      return;
    }

    this.update();

    // disable dropping of images to avoid memory issues with the container -
    // TODO: should eventually be solved with proper upload to S3
    this._editor.getEditor().container.ondrop = () => false;

    // We need to know when our iFrame looses focus, and manually remove focus from
    // Quill, to avoid it stealing focus when receiving updates from other users
    // ref: https://github.com/minervaproject/picasso/pull/5815
    window.addEventListener('message', (msg) => {
      if (
        msg.data === 'focus_state_controller:iframe-blurred' &&
        !readOnlyWorksheet
      ) {
        try {
          this._editor.getEditor().blur();
        } catch (e) {
          console.error('Could not getEditor().blur');
        }
      }
    });

    const { doc, focusLast, readOnlyWorksheet } = this.props;

    doc.on('op', this.onOp);
    this.setUpCursors();
    if (focusLast && !readOnlyWorksheet) {
      // only update cursor after we subscribe to the doc so the presence update gets synced
      const updateCursorInterval = setInterval(() => {
        if (doc.requestReplyPresence === false) {
          clearInterval(updateCursorInterval);
          const quill = this._editor.getEditor();
          quill.setSelection(quill.getLength());
          quill.focus();
          quill.enable();
          quill.setSelection(quill.getLength());
          this.updateCursor(quill.getSelection());
        }
      }, 100);
    }
  }

  onOp = (op, source) => {
    if (source === this._editor) {
      return;
    }

    if (this._editor) {
      const editor = this._editor.getEditor();
      if (!editor) {
        return;
      }

      const { currentUser } = this.props;

      forEach(op, (operation) => {
        // Ensures the ops are for exactly this editor in situations where there
        // are multiple active editors in the page
        if (isEqual(operation.p, this.path)) {
          editor.updateContents(operation.o);
        }
        if (operation.u !== currentUser.userId) {
          this.cursors.flashCursor(operation.u);
        }
      });
    }
  };

  handleOnChange = (content, delta, source) => {
    const {
      doc,
      currentUser,
      setChangedToNow = () => {},
      setSavedToNow = () => {},
    } = this.props;
    if (source === 'user') {
      const op = [
        {
          p: this.path,
          t: 'rich-text',
          o: delta.ops,
          u: currentUser.userId,
        },
      ];

      setChangedToNow();

      doc.submitOp(op, { source: this._editor }, (error) => {
        if (!error) {
          setSavedToNow();
        }
      });
    }
  };

  _storeRef = (ref) => {
    this._editor = ref;
    if (ref && ref.getEditor) {
      maybePatchQuillCompositionAPI(this._editor.getEditor());
    }
  };

  componentWillUnmount() {
    const { doc } = this.props;
    doc.removeListener('presence', this.onPresence);
    doc.removeListener('op', this.onOp);
  }

  render() {
    const {
      height,
      embedded,
      readOnlyWorksheet,
      captionsContent,
      darkMode,
      className,
      style,
      uuid,
      path,
      currentUser,
      data,
    } = this.props;

    const toolbarOptions = [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'link', 'blockquote', 'code-block'],
      [
        { list: 'ordered' },
        { list: 'bullet' },
        { indent: '-1' },
        { indent: '+1' },
        { color: [] },
      ],
    ];

    const modules =
      embedded || currentUser.rollup || readOnlyWorksheet || captionsContent
        ? { toolbar: false, cursors: true }
        : {
            cursors: true,
            syntax: true,
            toolbar: toolbarOptions,
          };

    const getClassName = () => {
      if (embedded) {
        return null;
      }
      if (currentUser.rollup) {
        return 'rollup body';
      }

      return classnames([
        height,
        className,
        { readonly: readOnlyWorksheet },
        { 'bg-black-tint-20': darkMode },
        { 'text-black-tint-70': darkMode },
        { 'background-light-blue': !darkMode },
        'rich-text-field mb4 rich-text secondary-font font-size-inherit rounded line-height-3',
      ]);
    };

    // Prevent automatic translation of Quill fields, as it wipes content that's not directly translatable
    // See https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/translate.
    return (
      <ReactQuill
        {...(currentUser.timeline && { value: data })}
        scrollingContainer="html"
        className={getClassName()}
        style={style}
        id={path}
        readOnly={readOnlyWorksheet}
        onChange={this.handleOnChange}
        ref={this._storeRef}
        modules={modules}
        formats={formats}
        translate="no"
      />
    );
  }
}

RichTextField.defaultProps = {
  className: '',
  height: 'medium',
  data: {},
  embedded: false,
  readOnlyWorksheet: false,
  captionsContent: false,
  darkMode: false,
  path: undefined,
  focusLast: undefined,
  style: null,
};

RichTextField.propTypes = {
  doc: PropTypes.object.isRequired,
  uuid: PropTypes.string.isRequired,
  currentUser: PropTypes.object.isRequired,
  data: PropTypes.object,
  height: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  readOnlyWorksheet: PropTypes.bool,
  captionsContent: PropTypes.bool,
  darkMode: PropTypes.bool,
  embedded: PropTypes.bool,
  path: PropTypes.arrayOf(PropTypes.string),
  focusLast: PropTypes.bool,
};

export default RichTextField;
